import React, { useState } from 'react';
import { Property } from '../types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../components/InfoTooltip';
export function PropertyInformation() {
  // Mock data for a Thailand resort
  const [property, setProperty] = useState<Property>({
    name: 'Siam Bay Tropical Paradise Resort',
    description: 'Nestled on the pristine shores of Koh Samui, Siam Bay Tropical Paradise Resort offers a perfect blend of Thai luxury and natural beauty. Surrounded by lush tropical gardens and overlooking the crystal-clear waters of the Gulf of Thailand, this exclusive retreat provides an authentic Thai experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Beach Access', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Thai Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: '124 Moo 3, Chaweng Beach Road',
      city: 'Koh Samui',
      postalCode: '84320',
      country: 'Thailand',
      latitude: '9.5120',
      longitude: '100.0136'
    },
    contact: {
      email: 'reservations@siambaytropical.com',
      phone: '+66 77 123 456',
      website: 'https://www.siambaytropicalresort.com',
      timezone: 'Asia/Bangkok'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Thai Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Thai Massage', 'Muay Thai Classes', 'Longtail Boat Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-4683">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-4684">
            {tag}
            <button className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-4685">
              <XIcon size={12} data-id="element-4686" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-4687">
      {/* Simplified header section */}
      <div data-id="element-4688">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-4689">Hotel</h1>
        <div className="flex justify-between items-center" data-id="element-4690">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-4691">
            <div className="flex space-x-6" data-id="element-4692">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-4693">
                Hotel Information
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-4694">
                Media assets
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-4695">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-4696">
              <span className="mr-2" data-id="element-4697">Draft: Submitted for review</span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-4698" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-4699">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-4700">
                Draft Status
              </h3>
              <p className="text-xs text-gray-600" data-id="element-4701">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-4702">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4703">
          <div data-id="element-4704">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4705">
              Hotel name
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-4706" />
          </div>
          <div data-id="element-4707">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4708">
              Designation
            </label>
            <div className="relative" data-id="element-4709">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-4710">
                <option value="Resort" data-id="element-4711">Resort</option>
                <option value="Hotel" data-id="element-4712">Hotel</option>
                <option value="Villa" data-id="element-4713">Villa</option>
                <option value="Guesthouse" data-id="element-4714">Guesthouse</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-4715">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4716" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-4717">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4718">
            Description
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-4719" />
        </div>
        <div className="mb-6" data-id="element-4720">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4721">
            Star rating
          </label>
          <div className="relative" data-id="element-4722">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-4723">
              <option value="5" data-id="element-4724">5</option>
              <option value="4" data-id="element-4725">4</option>
              <option value="3" data-id="element-4726">3</option>
              <option value="2" data-id="element-4727">2</option>
              <option value="1" data-id="element-4728">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-4729">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4730" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-4731">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4732">
            Tags
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4733">
          <div data-id="element-4734">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4735">
              Address
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-4736" />
          </div>
          <div data-id="element-4737">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4738">
              Area name
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Chaweng Beach" data-id="element-4739" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4740">
          <div data-id="element-4741">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4742">
              City
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-4743" />
          </div>
          <div data-id="element-4744">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4745">
              Postal code
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-4746" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-4747">
          <div data-id="element-4748">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4749">
              Country
            </label>
            <div className="relative" data-id="element-4750">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-4751">
                <option value="Thailand" data-id="element-4752">Thailand</option>
                <option value="Malaysia" data-id="element-4753">Malaysia</option>
                <option value="Indonesia" data-id="element-4754">Indonesia</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-4755">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4756" />
              </div>
            </div>
          </div>
          <div data-id="element-4757">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4758">
              Latitude
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-4759" />
          </div>
          <div data-id="element-4760">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4761">
              Longitude
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-4762" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4763">
          <div data-id="element-4764">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4765">
              Contact Email
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-4766" />
          </div>
          <div data-id="element-4767">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4768">
              Phone number
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-4769" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4770">
          <div data-id="element-4771">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4772">
              Website
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-4773" />
          </div>
          <div data-id="element-4774">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4775">
              Timezone
            </label>
            <div className="relative" data-id="element-4776">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-4777">
                <option value="Asia/Bangkok" data-id="element-4778">Asia/Bangkok</option>
                <option value="Asia/Kuala_Lumpur" data-id="element-4779">Asia/Kuala_Lumpur</option>
                <option value="Asia/Singapore" data-id="element-4780">Asia/Singapore</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-4781">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4782" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4783">
          <div data-id="element-4784">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4785">
              Check-in Time
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-4786" />
          </div>
          <div data-id="element-4787">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4788">
              Check-out Time
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-4789" />
          </div>
        </div>
        <div className="mb-6" data-id="element-4790">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4791">
            Hotel Amenities
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-4792">
            <div className="flex flex-wrap gap-2" data-id="element-4793">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-4794">
                  {amenity}
                  <button className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-4795">
                    <XIcon size={12} data-id="element-4796" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-4797">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4798">
            Transportation
          </label>
          <div className="space-y-2" data-id="element-4799">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-4800">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-4801" />
                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-4802">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-4803">
      <polyline points="6 9 12 15 18 9" data-id="element-4804"></polyline>
    </svg>;
}